/**
 * ControlGroup component
 * Renders the master device widget for a control group based on device type.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  ControlGroup,
  EnumDKeyDeviceType as EnumDeviceType,
  useToggleControlGroupAutomationMutation,
} from '../../../types/generated-types';
import type { Property, Unit } from '../../../types/generated-types';
import type { GroupingDevices } from '../deviceGroupings/device-grouping-types';
import BorderedSection from '../shared/borderedSection';
import { ActuatorComponent } from '../devices/central-equipment/actuator';
import { BoilerComponent } from '../devices/central-equipment/boiler';
import { ChillerComponent } from '../devices/central-equipment/chiller';
import { LoopComponent } from '../devices/central-equipment/loop';
import { GpioComponent } from '../devices/central-equipment/gpio';
import { PressureSensorComponent } from '../devices/central-equipment/pressureSensor';
import { PowerMonitorComponent } from '../devices/central-equipment/power-monitor';
import { ThermistorComponent } from '../devices/central-equipment/thermistor';
import { OpenWeatherComponent } from '../devices/central-equipment/openWeather';
import { WindowSensorComponent } from '../devices/residential-devices/window-sensor';
import { DoorSensorComponent } from '../devices/residential-devices/door-sensor';
import { OccupancySensorComponent } from '../devices/residential-devices/occupancy-sensor';
import { LeakDetectorSensorComponent } from '../devices/residential-devices/leak-detector-sensor';
import { SmartPowerOutletComponent } from '../devices/residential-devices/smart-power-outlet';
import { RelayDeviceComponent } from '../devices/residential-devices/relay-device';
import { ModbusAirHandlerComponent } from '../devices/modbus-air-handler/modbus-air-handler';
import { MultiSensorComponent } from '../devices/residential-devices/multi-sensor';
import { ThermostatComponent } from '../devices/residential-devices/thermostat/thermostat';
import { VsdPumpComponent } from '../devices/central-equipment/vsd-pump';
import { Z210PumpComponent } from '../devices/central-equipment/z-210-pump';
import {
  CubicFeetPerMinute,
  Fahrenheit,
} from '../../system/models/temperatureUnits';
import { useAuthenticator } from '../../auth/AuthenticationContext';
import { useAuthorizer } from '../../auth/AuthorizationContext';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Notifier } from '../../system/services/notificationManager';
import { ExpandSectionButton } from '../devices/shared-ui/alerts-info';
import { ManagedDevicesSection } from '../devices/shared-ui/managed-devices-section';

export interface ControlGroupProps {
  grouping: ControlGroup;
  groupingDevices: GroupingDevices;
  property: Partial<Property>;
  unit: Partial<Unit>;
}

export function ControlGroupComponent({
  grouping,
  groupingDevices,
  property,
  unit,
}: ControlGroupProps) {
  const { user } = useAuthenticator();
  const { can } = useAuthorizer();
  const isSmall = useIsSmallScreen();
  const preferredUnits = user?.preferredUnits ?? Fahrenheit;
  const preferredAirFlowUnits =
    user?.preferredAirFlowUnits ?? CubicFeetPerMinute;
  const masterDevice = grouping.masterDevice;
  const deviceType = masterDevice?.type;
  const deviceId = masterDevice?._id;

  const controlGroupId = grouping._id ?? '';
  const propertyIdForAuth = grouping.propertyId ?? property?._id ?? '';
  const canToggleAutomation =
    !!controlGroupId &&
    !!propertyIdForAuth &&
    can('controlGroupAutomation', {
      _id: propertyIdForAuth,
    } as Partial<Property>);

  const [automationEnabled, setAutomationEnabled] = useState(
    grouping.automationEnabled === true,
  );

  useEffect(() => {
    setAutomationEnabled(grouping.automationEnabled === true);
  }, [grouping._id, grouping.automationEnabled]);

  const [toggleAutomation, { loading: togglingAutomation }] =
    useToggleControlGroupAutomationMutation({
      onError: (err) => {
        Notifier.error(
          err.message ?? 'Failed to update control group automation.',
        );
        setAutomationEnabled(grouping.automationEnabled === true);
      },
    });

  const handleAutomationToggle = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (!controlGroupId || !canToggleAutomation || togglingAutomation) {
        return;
      }
      setAutomationEnabled(checked);
      toggleAutomation({
        variables: { id: controlGroupId },
        onCompleted: (data) => {
          const next = data.toggleControlGroupAutomation?.automationEnabled;
          if (typeof next === 'boolean') {
            setAutomationEnabled(next);
          }
        },
      });
    },
    [canToggleAutomation, controlGroupId, toggleAutomation, togglingAutomation],
  );

  const deviceDetailsForManaged = grouping.deviceDetails as
    | Record<string, unknown>
    | null
    | undefined;
  const hasManagedDevicesDetails =
    deviceDetailsForManaged != null &&
    typeof deviceDetailsForManaged === 'object' &&
    !Array.isArray(deviceDetailsForManaged);

  const [managedDevicesExpanded, setManagedDevicesExpanded] = useState(false);

  useEffect(() => {
    setManagedDevicesExpanded(false);
  }, [grouping._id]);

  const handleManagedDevicesToggle = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setManagedDevicesExpanded((prev) => !prev);
  }, []);

  if (!masterDevice || !deviceId) {
    return <div>No master device found</div>;
  }

  const renderMasterDeviceWidget = () => {
    switch (deviceType) {
      case EnumDeviceType.Thermostat:
        return (
          <ThermostatComponent
            id={deviceId}
            property={property}
            unit={unit}
            preferredUnits={preferredUnits}
          />
        );
      case EnumDeviceType.Actuator:
        return <ActuatorComponent deviceId={deviceId} />;
      case EnumDeviceType.Boiler:
        return (
          <BoilerComponent
            deviceId={deviceId}
            preferredUnits={preferredUnits}
          />
        );
      case EnumDeviceType.Chiller:
        return (
          <ChillerComponent
            deviceId={deviceId}
            preferredUnits={preferredUnits}
          />
        );
      case EnumDeviceType.Loop:
        return (
          <LoopComponent
            panelId={(grouping as ControlGroup)._id ?? ''}
            groupingDevices={groupingDevices ?? ({} as GroupingDevices)}
            preferredUnits={preferredUnits ?? Fahrenheit}
          />
        );
      case EnumDeviceType.Gpio:
        return (
          <GpioComponent
            deviceId={deviceId}
            role={
              masterDevice.name ?? (grouping as ControlGroup).name ?? 'GPIO'
            }
          />
        );
      case EnumDeviceType.PressureSensor:
        return (
          <PressureSensorComponent
            deviceId={deviceId}
            label={masterDevice.name ?? 'Pressure sensor'}
          />
        );
      case EnumDeviceType.PowerMonitor:
        return <PowerMonitorComponent deviceId={deviceId} />;
      case EnumDeviceType.Thermistor:
        return (
          <ThermistorComponent
            deviceId={deviceId}
            label={masterDevice.name ?? 'Thermistor'}
          />
        );
      case EnumDeviceType.OpenWeather:
        return (
          <OpenWeatherComponent
            deviceId={deviceId}
            label={masterDevice.name ?? 'Open weather'}
          />
        );
      case EnumDeviceType.WindowSensor:
        return <WindowSensorComponent deviceId={deviceId} />;
      case EnumDeviceType.DoorSensor:
        return <DoorSensorComponent deviceId={deviceId} />;
      case EnumDeviceType.OccupancySensor:
        return <OccupancySensorComponent deviceId={deviceId} />;
      case EnumDeviceType.LeakDetector:
        return <LeakDetectorSensorComponent deviceId={deviceId} />;
      case EnumDeviceType.SmartPowerOutlet:
        return (
          <SmartPowerOutletComponent
            deviceId={deviceId}
            pairedThermostat={groupingDevices?.thermostat}
          />
        );
      case EnumDeviceType.RelayDevice:
        return <RelayDeviceComponent deviceId={deviceId} />;
      case EnumDeviceType.ModbusAirHandler:
        return (
          <ModbusAirHandlerComponent
            id={deviceId}
            preferredUnits={preferredUnits}
            preferredAirFlowUnits={preferredAirFlowUnits}
            pairedThermostat={groupingDevices?.thermostat}
          />
        );
      case EnumDeviceType.IndoorAirHandler:
        return (
          <ModbusAirHandlerComponent
            id={deviceId}
            preferredUnits={preferredUnits}
            preferredAirFlowUnits={preferredAirFlowUnits}
            pairedThermostat={groupingDevices?.thermostat}
          />
        );
      case EnumDeviceType.IndoorSensor:
        return (
          <MultiSensorComponent
            deviceId={deviceId}
            preferredUnits={preferredUnits}
          />
        );
      case EnumDeviceType.Controller:
        return (
          <div>Unsupported master device type: Controller (no widget yet)</div>
        );
      case EnumDeviceType.VsdPump:
        return (
          <VsdPumpComponent deviceId={deviceId} editing={false} value={0} />
        );
      case EnumDeviceType.Z210Pump:
        return <Z210PumpComponent deviceId={deviceId} />;
      default:
        return (
          <div>
            {deviceType
              ? `Unsupported master device type: ${deviceType}`
              : 'No master device found'}
          </div>
        );
    }
  };

  return (
    <div
      className={`device-grouping-detail-${isSmall ? 'small' : 'large'}`}
      style={{
        width: isSmall ? '100%' : '400px',
        display: isSmall ? 'block' : 'flex',
        padding: isSmall ? '0px 2px 0px 3px' : '4px',
      }}
    >
      <BorderedSection
        containerStyle={{
          marginTop:
            groupingDevices.thermostat?._id ||
            groupingDevices.modbusAirHandler?._id
              ? '0px'
              : '14px',
        }}
        title={
          (groupingDevices.thermostat?._id ||
          groupingDevices.modbusAirHandler?._id
            ? ''
            : grouping.name
              ? grouping.name.split('_').join(' ')
              : 'Unnamed Zone') +
          (automationEnabled ? ' (Automated)' : ' (Manual)')
        }
        style={{
          width: '100%',
          maxWidth: isSmall ? 'unset' : '400px',
          margin: isSmall ? '0px 0px 12px 0px' : '0px',
        }}
        ccStyle={{
          padding: '3px',
          overflow: 'auto',
          marginTop: groupingDevices.thermostat?._id ? '0px' : '-6px',
        }}
        labelHeight={'20px'}
        actionable={hasManagedDevicesDetails}
        actionIcon={
          <ExpandSectionButton
            expand={managedDevicesExpanded}
            showRightDivider={false}
            buttonLabel={
              managedDevicesExpanded
                ? 'Hide extra content'
                : 'Show extra content'
            }
            onClick={handleManagedDevicesToggle}
            style={{ height: '25px' }}
          >
            <ExpandMoreIcon />
          </ExpandSectionButton>
        }
      >
        {canToggleAutomation ? (
          <div
            style={{
              width: '100%',
              minHeight: '32px',
              maxHeight: '40px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '4px',
              padding: '4px 8px',
              marginBottom: '4px',
              border: '1px solid darkgray',
              borderRadius: '4px',
              backgroundColor: automationEnabled ? 'lightgreen' : '#f5f5f5',
              overflow: 'hidden',
            }}
          >
            <FormControlLabel
              sx={{
                marginLeft: 0,
                marginRight: 0,
                flex: 1,
                minWidth: 0,
                justifyContent: 'space-between',
              }}
              control={
                <Switch
                  checked={automationEnabled}
                  onChange={handleAutomationToggle}
                  disabled={!canToggleAutomation || togglingAutomation}
                  size="small"
                  color="default"
                  sx={{
                    /* MUI lowers track opacity by default; force solid colors. */
                    '& .MuiSwitch-track': {
                      backgroundColor: '#bdbdbd !important',
                      opacity: '1 !important',
                    },
                    '& .MuiSwitch-switchBase': {
                      '&.Mui-checked': {
                        color: '#ffffff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#00e676 !important',
                          opacity: '1 !important',
                        },
                      },
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    pr: 1,
                  }}
                >
                  Enable control group automation
                </Typography>
              }
              labelPlacement="start"
            />
          </div>
        ) : null}
        {renderMasterDeviceWidget()}
        {hasManagedDevicesDetails ? (
          <Collapse in={managedDevicesExpanded} timeout="auto" unmountOnExit>
            <div style={{ width: '100%', paddingTop: '4px' }}>
              <ManagedDevicesSection
                data={deviceDetailsForManaged}
                controlGroupId={controlGroupId}
                propertyId={grouping.propertyId ?? property?._id ?? ''}
                unitId={grouping.unitId ?? unit?._id ?? ''}
              />
            </div>
          </Collapse>
        ) : null}
      </BorderedSection>
    </div>
  );
}
